import type { Tables, TablesInsert, TablesUpdate } from "@shared/database.types.ts";
import useSWR, { type SWRConfiguration } from "swr";
import { supabaseClient } from "@/lib/supabase";
import { useAuth } from "@/services/auth/auth-context";

export type UserSupa = Tables<"users">;
export type UserSupaInsert = TablesInsert<"users">;
export type UserSupaUpdate = TablesUpdate<"users">;
export type UserDetails = UserSupa & {
  family_members:
    | (Tables<"family_members"> & {
        families: Tables<"families"> | undefined;
      })
    | undefined;
};

// SWR configuration with deduplication and revalidation settings
const swrConfig: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  dedupingInterval: 600000, // 10 minute
  focusThrottleInterval: 30000, // 30 seconds
  errorRetryCount: 3,
  shouldRetryOnError: true,
};

import { CLIENTS_CACHE_KEYS } from "@/services/entities/cache-keys";

/**
 * Fetch a single client by ID
 * @param id Client ID
 * @returns Promise<Client>
 */
export async function fetchUser(id: string): Promise<UserSupa | null> {
  const { data, error } = await supabaseClient
    .from("users")
    .select("*")
    .eq("user_id", id)
    .maybeSingle();

  if (error) {
    console.error("Error fetching client", error);
    throw error;
  }
  return data;
}

export async function fetchUserDetails(userId: string) {
  const { data, error } = await supabaseClient
    .from("users")
    .select("*, family_members(*, families(*))")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    console.error("Error fetching client", error);
    throw error;
  }
  return data;
}

/**
 * Gets all clients. Should be used by admin only since this returns all clients, their emails, and other sensitive data.
 * @returns Promise<Client[]>
 */
export async function fetchAllClients() {
  const { data, error } = await supabaseClient
    .from("users")
    .select("*, family_members(*, families(*))")
    .eq("is_parent", true)
    .order("last_name");
  if (error) {
    console.error("Error fetching all clients", error);
    throw error;
  }
  return data ?? [];
}

export async function listUsers() {
  const { data, error } = await supabaseClient
    .from("users")
    .select("*, family_members(*, families(*))")
    .eq("is_active", true)
    .order("last_name");
  if (error) {
    console.error("Error fetching all clients", error);
    throw error;
  }
  return data ?? [];
}

/**
 * Searches for users not in a family by name or email.
 * @param searchTerm The term to search for.
 * @returns Promise<UserSupa[]>
 */
export async function searchUsersWithoutFamily(searchTerm: string): Promise<UserSupa[]> {
  if (!searchTerm) {
    return [];
  }

  const { data, error } = await supabaseClient
    .from("users")
    .select("*, family_members!left(*)")
    .is("family_members.family_id", null)
    .or(
      `first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`,
    )
    .limit(10);

  if (error) {
    console.error("Error searching users", error);
    throw error;
  }

  return data ?? [];
}

/**
 * Searches over users parents
 * @param searchTerm
 * @returns
 */
export async function searchParents(searchTerm: string): Promise<UserSupa[]> {
  if (!searchTerm) {
    return [];
  }

  const { data, error } = await supabaseClient
    .from("users")
    .select("*")
    .is("is_parent", true)
    .or(
      `first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`,
    )
    .limit(10);

  if (error) {
    console.error("Error searching users", error);
    throw error;
  }

  return data ?? [];
}

export async function fetchClientBySub(
  sub: string,
): Promise<(UserSupa & { family_members: { family_id: number } | null }) | null> {
  const { data, error } = await supabaseClient
    .from("users")
    .select("*, family_members(family_id)")
    .eq("cognito_sub", sub)
    .maybeSingle();

  if (error) {
    console.error("Error fetching client by sub", error);
    throw error;
  }
  return data;
}

/**
 * Hook for accessing a single client's information, for the current user
 * @returns Client data and loading/error states
 */
export function useMyClientData() {
  const authData = useAuth();
  const { data, error, isLoading, mutate } = useSWR(
    authData?.cognitoSub ? CLIENTS_CACHE_KEYS.MY_CLIENT_DATA(authData.cognitoSub) : null,
    () => fetchClientBySub(authData.cognitoSub!),
    {
      ...swrConfig,
    },
  );

  return {
    client: data,
    isLoading,
    error,
    refreshClient: mutate,
  };
}

export function useFamilyStudents(familyId: number | undefined) {
  const { data, error, isLoading, mutate } = useSWR(
    familyId ? `family-students-${familyId}` : null,
    async () => {
      if (!familyId) return [];
      const { data: members, error: memberError } = await supabaseClient
        .from("family_members")
        .select("users(*)")
        .eq("family_id", familyId)
        .eq("role", "child");

      if (memberError) throw memberError;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return members.map((m: any) => m.users).filter(Boolean);
    },
    swrConfig,
  );

  return {
    students: data as UserSupa[] | undefined,
    isLoading,
    error,
    refreshStudents: mutate,
  };
}

export async function updateUser(input: UserSupaUpdate) {
  if (!input.user_id) {
    throw new Error("Input doesn't have a user_id");
  }
  const { data, error } = await supabaseClient
    .from("users")
    .update(input)
    .eq("user_id", input.user_id)
    .select()
    .single();

  if (error) {
    throw new Error(error.message);
  }
  return data;
}

/**
 *
 * @param id The Id of a single client
 * @returns
 */
export function useClient(id: string) {
  const { data, error, isLoading, mutate } = useSWR(
    CLIENTS_CACHE_KEYS.CLIENT(id),
    () => fetchUser(id),
    swrConfig,
  );

  const updateClient = async (input: UserSupaUpdate) => {
    const { data: updatedUser, error } = await supabaseClient
      .from("users")
      .update(input)
      .eq("user_id", id)
      .select()
      .single();

    if (error) {
      console.error("Error updating client", error);
      throw error;
    }

    mutate(updatedUser);
    return updatedUser;
  };

  return {
    client: data,
    isLoading,
    error,
    refreshClient: mutate,
    updateClient,
  };
}

export function useClients() {
  const { data, error, isLoading, mutate } = useSWR<UserSupa[]>(
    CLIENTS_CACHE_KEYS.CLIENTS,
    fetchAllClients,
    swrConfig,
  );

  const deactivateClient = async (id: string) => {
    const { error } = await supabaseClient
      .from("users")
      .update({ is_active: false })
      .eq("user_id", id);

    if (error) {
      console.error("Error deleting client", error);
      throw error;
    }

    mutate();
    return;
  };

  return {
    clients: data || [],
    isLoading,
    error,
    refreshClients: mutate,
    deactivateClient,
  };
}

export function useUsers() {
  const { data, error, isLoading, mutate } = useSWR<UserSupa[]>(
    CLIENTS_CACHE_KEYS.USERS,
    listUsers,
    swrConfig,
  );

  const deactivateUser = async (id: string) => {
    const { error } = await supabaseClient
      .from("users")
      .update({ is_active: false })
      .eq("user_id", id);

    if (error) {
      console.error("Error deleting user", error);
      throw error;
    }

    mutate();
    return;
  };

  return {
    users: data || [],
    isLoading,
    error,
    refreshUsers: mutate,
    deactivateUser,
  };
}
